import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Divider, Grid, Stack, Typography } from "@mui/material";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { Button } from "Components/_Shared/Buttons/Button";
import { Flex } from "Components/_Shared/Core/Flex";
import { Loader } from "Components/_Shared/Loader";
import { useGetAnalysisPortfolios, useListContractProposals } from "Infrastructure/ApiZfp/AnalyzerApi";
import type { ContractProposalListItemHorizonType } from "Infrastructure/ApiZfp/Schema/contractProposalListItemHorizonType";
import { NsurePlanOverviewHorizon } from "Infrastructure/ApiZfp/Schema/nsurePlanOverviewHorizon";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext } from "react";
import { InvestmentsNsureCustomSolutionPanel } from "./InvestmentsNsureCustomSolutionPanel";
import { InvestmentsNsureInstrumentSearch } from "./InvestmentsNsureInstrumentSearch";
import { InvestmentsNsurePortfolioCard } from "./InvestmentsNsurePortfolioCard";
import { NsureSelectionContext, NsureSelectionItemType } from "./InvestmentsNsureSelectionContext";
import { mapToStrategyInstruments } from "./helpers";

const NSURE_HORIZON_TO_CONTRACT_HORIZON: Partial<Record<NsurePlanOverviewHorizon, ContractProposalListItemHorizonType>> = {
  [NsurePlanOverviewHorizon.short]: "short_term",
  [NsurePlanOverviewHorizon.middle]: "mid_term",
  [NsurePlanOverviewHorizon.long]: "long_term",
};

// Header row height (its minHeight below) plus this column's own top padding and the gap under the
// header — the evaluation panel offsets itself by this much so it starts at the content line, not at
// the column title.
export const SOLUTION_SELECT_HEADER_OFFSET = 10 + Spacing.L + Spacing.XL;

export const InvestmentsNsureSolutionSelect = () => {
  const { t } = useResource();
  const { selection, setSelection, showInstrumentSearch, setShowInstrumentSearch, nsurePlan } = useContext(NsureSelectionContext);

  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const { data: portfoliosData, isPending } = useGetAnalysisPortfolios(analysisId, versionId);
  const { data: contractProposalsData, isPending: isProposalsPending } = useListContractProposals(analysisId, versionId);

  const currentHorizonType = nsurePlan ? NSURE_HORIZON_TO_CONTRACT_HORIZON[nsurePlan.horizon] : undefined;

  const usedStrategyTemplateIds = new Set(
    (contractProposalsData?.items ?? [])
      .filter((p) => p.portfolio?.strategyTemplateID != null && (currentHorizonType == null || p.horizonType === currentHorizonType))
      .map((p) => p.portfolio!.strategyTemplateID),
  );

  const portfolioList =
    (portfoliosData ?? []).find((portfolio) => portfolio.groupID === nsurePlan?.nsureInvestmentPlanExternalId)?.investmentStrategies ?? [];

  const portfolioListTemplates = portfolioList.filter((portfolio) => portfolio.strategyTemplateID !== null);

  const selectedPortfolioId = selection?.type === NsureSelectionItemType.Portfolio ? selection.id : null;

  const handleClickChangeSelect = () => {
    setShowInstrumentSearch((prev) => !prev);
    setSelection(null);
  };

  return (
    <Stack spacing={Spacing.XL} p={Spacing.XL} pt={Spacing.L} sx={{ flex: 1, minWidth: 0 }}>
      {isPending || isProposalsPending ? (
        <Loader />
      ) : (
        <>
          <Flex justifyContent="space-between" alignItems="center" sx={(theme) => ({ minHeight: theme.spacing(10) })}>
            <Typography variant="body1" fontWeight={700}>
              {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.SolutionSelect.Title)}
            </Typography>
            {showInstrumentSearch && (
              <Button
                variant="contained"
                color="secondary"
                onClick={handleClickChangeSelect}
                endAdornment={<KeyboardArrowRightIcon fontSize="small" sx={{ position: "relative", top: "-1px" }} />}
              >
                {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.SolutionSelect.ProposedStrategies)}
              </Button>
            )}
          </Flex>

          {showInstrumentSearch ? (
            <InvestmentsNsureInstrumentSearch />
          ) : (
            <>
              <InvestmentsNsureCustomSolutionPanel onClick={handleClickChangeSelect} />

              <Divider />

              <Grid container spacing={Spacing.L}>
                {portfolioListTemplates.map((portfolio) => {
                  const portfolioId = portfolio.strategyTemplateID;
                  return (
                    <Grid key={portfolioId} size={{ xs: 12, sm: 6 }}>
                      <InvestmentsNsurePortfolioCard
                        portfolio={portfolio}
                        selected={selectedPortfolioId === portfolio.strategyTemplateID}
                        disabled={
                          portfolio.strategyTemplateID != null &&
                          usedStrategyTemplateIds.has(portfolio.strategyTemplateID) &&
                          selectedPortfolioId !== portfolio.strategyTemplateID
                        }
                        onClick={() =>
                          setSelection({
                            id: portfolio.strategyTemplateID,
                            type: NsureSelectionItemType.Portfolio,
                            expectedPerformance: portfolio.expectedPerformance ?? null,
                            name: portfolio.name,
                            esg: portfolio.esg ?? null,
                            sri: portfolio.sri,
                            srri: portfolio.srri,
                            instrumentType: null,
                            strategyRiskType: portfolio.strategyRiskType,
                            strategyInstruments: portfolio.strategyInstruments
                              ? mapToStrategyInstruments(portfolio.strategyInstruments)
                              : null,
                            strategyInstrumentsEvaluation: portfolio.strategyInstrumentsEvaluation
                              ? mapToStrategyInstruments(portfolio.strategyInstrumentsEvaluation)
                              : null,
                          })
                        }
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}
        </>
      )}
    </Stack>
  );
};
